'use client';

/**
 * AuditDiffViewer Component
 *
 * Side-by-side or inline comparison of two entity versions,
 * with field-by-field change highlighting.
 *
 * @see Issue #234 - Entity Audit Activity Logs
 */

import AddIcon from '@mui/icons-material/Add';
import CompareIcon from '@mui/icons-material/CompareArrows';
import RemoveIcon from '@mui/icons-material/Remove';
import ModifyIcon from '@mui/icons-material/SwapHoriz';
import { Box, Chip, Collapse, IconButton, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { NeonAccent } from '../../theme/palette';
import { baseColors } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';
import type { AuditDiffViewerProps, FieldDiff } from '../../types/audit';
import { GlassCard } from '../GlassCard/GlassCard';
import { GlassTooltip } from '../GlassTooltip';
import { LoadingOverlay } from '../LoadingOverlay';

// ============================================================================
// Helper Functions
// ============================================================================

function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '(empty)';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

function getChangeColor(changeType: FieldDiff['changeType']): string {
  switch (changeType) {
    case 'added':
      return baseColors.success[500];
    case 'removed':
      return baseColors.error[500];
    case 'modified':
      return baseColors.info[500];
    default:
      return baseColors.neutral[500];
  }
}

function getChangeIcon(changeType: FieldDiff['changeType']) {
  switch (changeType) {
    case 'added':
      return <AddIcon fontSize="small" />;
    case 'removed':
      return <RemoveIcon fontSize="small" />;
    case 'modified':
      return <ModifyIcon fontSize="small" />;
    default:
      return null;
  }
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const StyledVersionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5),
  borderRadius: 8,
  marginBottom: theme.spacing(2),
}));

interface StyledDiffContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledDiffContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledDiffContainerProps>(({ $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    borderRadius: 12,
    background: glass.background,
    border: `1px solid ${glass.border}`,
    overflow: 'hidden',
  };
});

interface StyledFieldRowProps {
  $changeType: FieldDiff['changeType'];
  $isDark: boolean;
}

const StyledFieldRow = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledFieldRowProps>(({ theme, $changeType, $isDark }) => {
  const bgAlpha = $isDark ? '0.1' : '0.05';
  const borderAlpha = $isDark ? '0.3' : '0.2';
  const color = getChangeColor($changeType);

  return {
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: `${color}${bgAlpha.replace('.', '')}`,
    borderLeft: `3px solid ${color}${borderAlpha.replace('.', '')}`,

    '&:last-child': {
      borderBottom: 'none',
    },
  };
});

const StyledFieldName = styled(Box)(({ theme }) => ({
  width: 200,
  minWidth: 200,
  padding: theme.spacing(1.5),
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontWeight: 600,
  fontSize: '0.875rem',
}));

const StyledFieldValue = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1.5),
  fontFamily: 'monospace',
  fontSize: '0.8125rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const StyledSideBySide = styled(Box)({
  display: 'flex',

  '& > div': {
    flex: 1,
  },
});

const StyledSummary = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const StyledEmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
}));

// ============================================================================
// Component
// ============================================================================

export function AuditDiffViewer({
  comparison,
  loading = false,
  error,
  mode = 'inline',
  changesOnly: _changesOnly = true,
  expandAll = false,
  onFieldClick,
  variant = 'glass',
  neonAccent,
  title,
}: AuditDiffViewerProps) {
  // changesOnly is kept for API compatibility but filtering happens at data layer
  void _changesOnly;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  // Toggle field expansion
  const toggleField = (field: string) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(field)) {
      newExpanded.delete(field);
    } else {
      newExpanded.add(field);
    }
    setExpandedFields(newExpanded);
  };

  // Check if a field value is complex (object/array)
  const isComplexValue = (value: unknown): boolean => {
    return typeof value === 'object' && value !== null;
  };

  // Render content
  const renderContent = () => {
    // Error state
    if (error) {
      return (
        <StyledEmptyState>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </StyledEmptyState>
      );
    }

    // Loading state
    if (loading) {
      return (
        <Box sx={{ position: 'relative', minHeight: 160 }}>
          <LoadingOverlay visible message={t('common.loading')} size={32} neonAccent={neonAccent} />
        </Box>
      );
    }

    // No comparison data
    if (!comparison) {
      return (
        <StyledEmptyState>
          <CompareIcon sx={{ fontSize: iconSizes['3xl'], color: 'text.disabled', mb: 2 }} />
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('audit.diff.selectVersions')}
          </Typography>
        </StyledEmptyState>
      );
    }

    // No changes
    if (comparison.changes.length === 0) {
      return (
        <StyledEmptyState>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('audit.diff.noChanges')}
          </Typography>
        </StyledEmptyState>
      );
    }

    // Render diff based on mode
    if (mode === 'side-by-side') {
      return (
        <>
          {/* Version Headers */}
          <StyledSideBySide>
            <StyledVersionHeader sx={{ bgcolor: 'error.main', color: 'error.contrastText', mr: 0.5 }}>
              <Typography variant="subtitle2">{t('audit.diff.from')}</Typography>
              <Typography variant="caption">{comparison.versionA.createdAt}</Typography>
            </StyledVersionHeader>
            <StyledVersionHeader sx={{ bgcolor: 'success.main', color: 'success.contrastText', ml: 0.5 }}>
              <Typography variant="subtitle2">{t('audit.diff.to')}</Typography>
              <Typography variant="caption">{comparison.versionB.createdAt}</Typography>
            </StyledVersionHeader>
          </StyledSideBySide>
          {/* Diff Table */}
          <StyledDiffContainer $isDark={isDark} $neonAccent={neonAccent}>
            {comparison.changes.map((change) => {
              const isComplex = isComplexValue(change.oldValue) || isComplexValue(change.newValue);
              const isExpanded = expandAll || expandedFields.has(change.field);

              return (
                <StyledFieldRow key={change.field} $changeType={change.changeType} $isDark={isDark}>
                  <StyledFieldName>
                    <Box sx={{ color: getChangeColor(change.changeType) }}>{getChangeIcon(change.changeType)}</Box>
                    <Typography
                      variant="body2"
                      sx={{ cursor: isComplex ? 'pointer' : 'default' }}
                      onClick={() => isComplex && toggleField(change.field)}
                    >
                      {change.field}
                    </Typography>
                    {onFieldClick && (
                      <GlassTooltip title={t('common.view')}>
                        <IconButton size="small" onClick={() => onFieldClick(change)}>
                          <CompareIcon fontSize="inherit" />
                        </IconButton>
                      </GlassTooltip>
                    )}
                  </StyledFieldName>
                  <StyledSideBySide>
                    <StyledFieldValue
                      sx={{
                        bgcolor: 'error.main',
                        opacity: 0.1,
                        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {isComplex && !isExpanded ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {t('common.clickToExpand')}
                        </Typography>
                      ) : (
                        <Collapse in={!isComplex || isExpanded}>{formatValue(change.oldValue)}</Collapse>
                      )}
                    </StyledFieldValue>
                    <StyledFieldValue sx={{ bgcolor: 'success.main', opacity: 0.1 }}>
                      {isComplex && !isExpanded ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {t('common.clickToExpand')}
                        </Typography>
                      ) : (
                        <Collapse in={!isComplex || isExpanded}>{formatValue(change.newValue)}</Collapse>
                      )}
                    </StyledFieldValue>
                  </StyledSideBySide>
                </StyledFieldRow>
              );
            })}
          </StyledDiffContainer>
          {/* Summary */}
          <StyledSummary>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('audit.diff.summary')}:
            </Typography>
            <Chip
              icon={<AddIcon fontSize="small" />}
              label={`${comparison.summary.fieldsAdded} ${t('audit.diff.added')}`}
              size="small"
              color="success"
              variant="outlined"
            />
            <Chip
              icon={<RemoveIcon fontSize="small" />}
              label={`${comparison.summary.fieldsRemoved} ${t('audit.diff.removed')}`}
              size="small"
              color="error"
              variant="outlined"
            />
            <Chip
              icon={<ModifyIcon fontSize="small" />}
              label={`${comparison.summary.fieldsModified} ${t('audit.diff.modified')}`}
              size="small"
              color="info"
              variant="outlined"
            />
          </StyledSummary>
        </>
      );
    }

    // Inline/Unified mode
    return (
      <StyledDiffContainer $isDark={isDark} $neonAccent={neonAccent}>
        {comparison.changes.map((change) => (
          <StyledFieldRow key={change.field} $changeType={change.changeType} $isDark={isDark}>
            <StyledFieldName>
              <Box sx={{ color: getChangeColor(change.changeType) }}>{getChangeIcon(change.changeType)}</Box>
              <Typography variant="body2">{change.field}</Typography>
            </StyledFieldName>
            <StyledFieldValue>
              {change.changeType === 'modified' ? (
                <Box>
                  <Typography variant="caption" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
                    {formatValue(change.oldValue)}
                  </Typography>
                  <Typography variant="caption" sx={{ mx: 1 }}>
                    →
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'success.main' }}>
                    {formatValue(change.newValue)}
                  </Typography>
                </Box>
              ) : change.changeType === 'added' ? (
                <Typography variant="caption" sx={{ color: 'success.main' }}>
                  + {formatValue(change.newValue)}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
                  - {formatValue(change.oldValue)}
                </Typography>
              )}
            </StyledFieldValue>
          </StyledFieldRow>
        ))}
      </StyledDiffContainer>
    );
  };

  // Main component
  const diffViewer = (
    <Box>
      {/* Header */}
      {title && (
        <StyledHeader>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
        </StyledHeader>
      )}

      {/* Content */}
      {renderContent()}
    </Box>
  );

  // Wrap in card if needed
  if (variant === 'glass') {
    return <GlassCard neonAccent={neonAccent}>{diffViewer}</GlassCard>;
  }

  return diffViewer;
}

AuditDiffViewer.displayName = 'AuditDiffViewer';

export default AuditDiffViewer;
